// Lógica pura de la pestaña "Roles y permisos": agrupación del catálogo,
// búsqueda, diff de permisos y rotulado. Sin React ni red, para poder probarla
// sola.

/** Tipos de entrada del listado (`kind`) tal como los entrega GET /users/roles. */
export const ROLE_KINDS = { BASE: "base", OVERRIDE: "override", CUSTOM: "custom" };

/**
 * Tipo legible de una entrada: Base / Personalizado de base / Propio.
 * @param {string} kind
 * @param {(key: string) => string} t
 */
export function roleKindLabel(kind, t) {
  if (kind === ROLE_KINDS.OVERRIDE) return t("Personalizado de base");
  if (kind === ROLE_KINDS.CUSTOM) return t("Propio");
  return t("Base");
}

/**
 * Nombre legible de un módulo del catálogo. El backend manda el id del módulo
 * (p. ej. "pedidos_operacion"); un id desconocido se humaniza en vez de
 * mostrarse crudo, y un texto que ya viene legible pasa tal cual.
 * @param {string} moduleId
 * @param {(key: string) => string} t
 */
export function moduleLabel(moduleId, t) {
  switch (moduleId) {
    case "analisis_informes": return t("Análisis e informes");
    case "app_repartidores": return t("App de repartidores");
    case "auth_cuenta": return t("Cuenta y acceso");
    case "facturacion": return t("Facturación");
    case "flota_gps": return t("Flota y GPS");
    case "gestion": return t("Gestión");
    case "holding": return t("Holding");
    case "integraciones_faqs": return t("Integraciones y FAQs");
    case "pedidos_operacion": return t("Pedidos y operación");
    case "recolecciones_retiros": return t("Recolecciones y retiros");
    case "seguimiento_vivo": return t("Seguimiento en vivo");
    case "soporte": return t("Soporte");
    case "": case undefined: case null: return t("Otros");
    default: {
      const id = String(moduleId);
      if (!/^[a-z0-9_]+$/.test(id)) return id;
      const words = id.replace(/_+/g, " ").trim();
      return words.charAt(0).toUpperCase() + words.slice(1);
    }
  }
}

/**
 * Agrupa el catálogo plano por módulo, conservando el orden de llegada. Cada
 * grupo trae la unión de acciones de sus recursos (las columnas de la matriz).
 * @param {Array<{resource: string, label?: string, module?: string, actions?: Array}>} resources
 * @param {(key: string) => string} t
 */
export function groupCatalog(resources, t) {
  const byModule = new Map();
  for (const res of Array.isArray(resources) ? resources : []) {
    const moduleId = res?.module ?? "";
    if (!byModule.has(moduleId)) {
      byModule.set(moduleId, { module: moduleId, label: moduleLabel(moduleId, t), resources: [], actions: [] });
    }
    const group = byModule.get(moduleId);
    const actions = Array.isArray(res?.actions) ? res.actions : [];
    group.resources.push({ ...res, label: res.label || res.resource, actions });
    for (const a of actions) {
      if (!group.actions.some((x) => x.action === a.action)) {
        group.actions.push({ action: a.action, label: a.label || a.action });
      }
    }
  }
  return Array.from(byModule.values());
}

/**
 * Filtra los grupos por texto (módulo, recurso, clave o acción). Si coincide
 * el módulo se conserva entero; si no, solo los recursos que coinciden.
 */
export function filterGroups(groups, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return groups;
  const out = [];
  for (const group of groups) {
    if (group.label.toLowerCase().includes(q)) {
      out.push(group);
      continue;
    }
    const resources = group.resources.filter(
      (res) =>
        res.label.toLowerCase().includes(q) ||
        String(res.resource).toLowerCase().includes(q) ||
        res.actions.some(
          (a) => String(a.label).toLowerCase().includes(q) || String(a.key).toLowerCase().includes(q),
        ),
    );
    if (resources.length) out.push({ ...group, resources });
  }
  return out;
}

/** Mapa clave → { grantable, label } de todas las acciones del catálogo. */
export function indexCatalog(resources) {
  const index = new Map();
  for (const res of Array.isArray(resources) ? resources : []) {
    for (const a of Array.isArray(res?.actions) ? res.actions : []) {
      index.set(a.key, { grantable: a.grantable === true, label: `${res.label || res.resource}: ${a.label || a.action}` });
    }
  }
  return index;
}

/**
 * Diferencia entre los permisos guardados y el borrador.
 * @param {string[]} original
 * @param {Set<string>} draft
 * @returns {{ added: string[], removed: string[] }}
 */
export function diffPermissions(original, draft) {
  const before = new Set(original);
  return {
    added: Array.from(draft).filter((k) => !before.has(k)).sort(),
    removed: original.filter((k) => !draft.has(k)).sort(),
  };
}

/** Rol base del que depende una entrada (id del rol base global). */
export const baseIdOf = (role) => role?.baseRoleId ?? null;
